/**
 * EditorialEmptyState — the composable empty-state primitive used by every
 * admin/dashboard zero-data view.  Slot for a small gold-line illustration,
 * an italic display headline, a quiet descriptor, a primary CTA and a tiny
 * secondary link.
 *
 * @example
 *   <EditorialEmptyState
 *     icon={<CameraSvg />}
 *     headline={<>Awaiting its <em>first portrait</em>.</>}
 *     description="Create a gallery to share with your client."
 *     primary={{ label: "New gallery", href: "/admin/galleries/new" }}
 *     secondary={{ label: "Read the gallery guide", href: "/help/galleries" }}
 *   />
 */
import React from "react";
import DisplayHeadline from "./DisplayHeadline";
import GoldRule from "./GoldRule";
import GoldFoilButton from "./GoldFoilButton";
import HairlineButton from "./HairlineButton";
import LottieEditorial from "./LottieEditorial";

export default function EditorialEmptyState({
  icon = null,
  lottie = null,    // optional Lottie JSON animation data; renders centered foreground over backdrop
  lottieLoop = false, // empty-state lotties play once on mount and rest on the final frame
  eyebrow,
  headline,
  description,
  primary,
  secondary,
  tone = "cream",  // cream | charcoal
  backdrop = null, // editorial asset slug to use as faded background plate
  className = "",
  ...rest
}) {
  const isDark = tone === "charcoal";
  return (
    <div
      className={`relative mx-auto max-w-xl text-center py-16 px-6 ${isDark ? "text-[color:var(--cream)]" : "text-[color:var(--charcoal)]"} ${className}`}
      {...rest}
    >
      {/* Phase 1 — editorial backdrop plate. Low-opacity charcoal-tinted image
          sits behind the empty-state copy; Lottie animations will layer on
          top of it in Phase 4. */}
      {backdrop ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(${isDark ? "rgba(14,13,12,0.78)" : "rgba(250,249,246,0.82)"}, ${isDark ? "rgba(14,13,12,0.92)" : "rgba(250,249,246,0.96)"}), url(${backdrop})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.85,
          }}
          data-testid="empty-state-backdrop"
        />
      ) : null}
      {lottie ? (
        <div className="mx-auto mb-7 inline-flex items-center justify-center">
          <LottieEditorial
            animation={lottie}
            size={140}
            loop={lottieLoop}
            autoplay
            reducedMotionFallback={icon ? (
              <span className="inline-flex items-center justify-center w-16 h-16 text-[color:var(--gold-base)]">{icon}</span>
            ) : null}
            data-testid="empty-state-lottie"
          />
        </div>
      ) : icon ? (
        <div className="mx-auto mb-8 inline-flex items-center justify-center w-16 h-16 text-[color:var(--gold-base)]">
          {icon}
        </div>
      ) : null}
      {eyebrow ? (
        <p className="ed-eyebrow text-[color:var(--gold-base)] mb-5">{eyebrow}</p>
      ) : null}
      <DisplayHeadline as="h2" size="section" align="center" className="mb-5">
        {headline}
      </DisplayHeadline>
      <GoldRule width="3rem" align="center" className="my-5" />
      {description ? (
        <p className={`ed-body-lg max-w-md mx-auto ${isDark ? "opacity-80" : "text-[color:var(--charcoal)]/70"}`}>
          {description}
        </p>
      ) : null}
      {(primary || secondary) ? (
        <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-5">
          {primary ? (
            <GoldFoilButton href={primary.href} onClick={primary.onClick} tone={isDark ? "dark" : "light"}>
              {primary.label}
            </GoldFoilButton>
          ) : null}
          {secondary ? (
            <HairlineButton href={secondary.href} onClick={secondary.onClick} arrow={false} size="sm" tone={isDark ? "dark" : "light"}>
              {secondary.label}
            </HairlineButton>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
